import React from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { azureClientId } from "config";

const scopes = [`${azureClientId}/.default`];

/**
 * This hook returns a function that can be used to get an access token for the
 * given scopes. Used to be called useAccessToken() and return an accessToken
 * but it turns out MSAL does not trigger a re-render when obtaining a refresh
 * token causing the accessToken to become stale. This hook is a workaround.
 * Taken from:
 *  https://dsigitlab.research.auto.pioneer.com/data-platform/galileo-frontend/-/merge_requests/184
 */
export default function useGetAccessToken() {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || null);

  const getAccessToken = React.useCallback(async () => {
    if (account) {
      const request = {
        scopes,
        account,
      };

      try {
        const response = await instance.acquireTokenSilent(request);
        return response.accessToken;
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          // Fallback to popup if silent acquisition fails.
          // This is a worst case scenario if acquireTokenSilent fails for some reason
          const response = await instance.acquireTokenPopup(request);
          return response.accessToken;
        }
        throw e;
      }
    }
    throw new Error("No accounts found");
  }, [instance, account]);

  return getAccessToken;
}
